import { MARK_OPEN, TABLE_ROW, wrapMark } from "./rules-marks";
import { mergeTables } from "./rules-tables";

// The marks are part of this module's contract: callers that render a diff
// need them to strip or style what it marked.
export { DROP_CLOSE, DROP_OPEN, MARK_CLOSE, MARK_OPEN } from "./rules-marks";

const HEADING = /^#{1,6}\s+(.*)$/;

/**
 * Line-pair ceiling for the quadratic walk. The largest written plan in the
 * corpus is ~5,400 lines, and comparing two of those would allocate tens of
 * millions of cells on the main thread. Past this ceiling the page says so
 * instead of freezing the tab.
 */
const MAX_COMPARED_CELLS = 2_000_000;

export type DiffKind = "same" | "removed" | "added" | "changed";

/**
 * A passage as the reader sees it, for searching.
 *
 * The lines are markdown carrying the diff's own sentinels, so searching them
 * raw missed every phrase that crossed a mark: "rate is 30" does not appear in
 * "rate is **30**", and neither does it in a table cell, where the pipes sit
 * between the words. Only the markup is taken out. Punctuation the reader can
 * see, such as a dollar sign or a percent, is part of what they are looking
 * for.
 */
export const passageText = (lines: string[]) =>
  lines
    .join(" ")
    .replace(/[\u2062\u2063\u2064\u206a]/g, "")
    .replace(/^\s*#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/[*_`~|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** A run of consecutive lines with the same fate, rendered as one passage. */
export interface DiffBlock {
  kind: DiffKind;
  lines: string[];
  /** Index of the change this block belongs to, for cycling. */
  change: number | null;
  /** Nearest markdown heading above it, for orientation. */
  section: string;
}

const lines = (value: string | null | undefined) =>
  (value ?? "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.trimEnd());

/**
 * Longest-common-subsequence walk over lines. The written pay plan is a few
 * hundred lines, so the quadratic table is cheap and keeps each changed passage
 * aligned with the wording around it.
 */
const walk = (before: string[], after: string[]) => {
  const rows: number[][] = new Array(before.length + 1);
  rows[before.length] = new Array<number>(after.length + 1).fill(0);
  for (let i = before.length - 1; i >= 0; i -= 1) {
    const row = new Array<number>(after.length + 1).fill(0);
    for (let j = after.length - 1; j >= 0; j -= 1)
      row[j] =
        before[i] === after[j]
          ? rows[i + 1][j + 1] + 1
          : Math.max(rows[i + 1][j], row[j + 1]);
    rows[i] = row;
  }
  const steps: { kind: "same" | "removed" | "added"; line: string }[] = [];
  let i = 0;
  let j = 0;
  while (i < before.length && j < after.length) {
    if (before[i] === after[j]) {
      steps.push({ kind: "same", line: before[i] });
      i += 1;
      j += 1;
    } else if (rows[i + 1][j] >= rows[i][j + 1]) {
      steps.push({ kind: "removed", line: before[i] });
      i += 1;
    } else {
      steps.push({ kind: "added", line: after[j] });
      j += 1;
    }
  }
  for (; i < before.length; i += 1)
    steps.push({ kind: "removed", line: before[i] });
  for (; j < after.length; j += 1)
    steps.push({ kind: "added", line: after[j] });
  return steps;
};

/** Word tokens, keeping the whitespace so a line can be put back together. */
const words = (line: string) =>
  line.split(/(\s+)/).filter((part) => part !== "");

/**
 * Marks the words that differ between two versions of one line.
 *
 * A reworded sentence inside a long paragraph is the common case in a pay plan,
 * and colouring the whole paragraph red and green says no more than "this
 * paragraph changed" — which is what the reader already knew.
 */
const markWords = (
  before: string,
  after: string,
  keep: "removed" | "added",
) => {
  const a = words(before);
  const b = words(after);
  const rows: number[][] = new Array(a.length + 1);
  rows[a.length] = new Array<number>(b.length + 1).fill(0);
  for (let i = a.length - 1; i >= 0; i -= 1) {
    const row = new Array<number>(b.length + 1).fill(0);
    for (let j = b.length - 1; j >= 0; j -= 1)
      row[j] =
        a[i] === b[j]
          ? rows[i + 1][j + 1] + 1
          : Math.max(rows[i + 1][j], row[j + 1]);
    rows[i] = row;
  }
  const out: string[] = [];
  const mine = keep === "removed" ? a : b;
  let i = 0;
  let j = 0;
  let marked: string[] = [];
  const flush = () => {
    if (!marked.length) return;
    out.push(wrapMark(marked.join("")));
    marked = [];
  };
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      flush();
      out.push(a[i]);
      i += 1;
      j += 1;
    } else if (rows[i + 1][j] >= rows[i][j + 1]) {
      if (keep === "removed") marked.push(a[i]);
      i += 1;
    } else {
      if (keep === "added") marked.push(b[j]);
      j += 1;
    }
  }
  for (; i < a.length; i += 1) if (keep === "removed") marked.push(a[i]);
  for (; j < b.length; j += 1) if (keep === "added") marked.push(b[j]);
  flush();
  // Whitespace matches between any two lines, so a passage that was rewritten
  // outright still comes back as every word marked with unmarked spaces between
  // them. That is noise: the passage's own colour already says it changed.
  const kept = out
    .filter((part) => !part.startsWith(MARK_OPEN))
    .flatMap((part) => words(part))
    .filter((word) => word.trim()).length;
  return kept === 0 ? mine.join("") : out.join("");
};

/**
 * The whole written pay plan as one document, with the passages that moved
 * marked in place. Reading a change beside the wording it sits in is the point:
 * an extracted list of before-and-after passages loses where they belong.
 */
export const rulesDocumentDiff = (
  current: string | null | undefined,
  proposed: string | null | undefined,
): {
  blocks: DiffBlock[];
  changes: number;
  comparable: boolean;
  /** Lines the proposal adds and takes away, counted before blocks merge. */
  added: number;
  removed: number;
} => {
  const before = lines(current);
  const after = lines(proposed);
  if (before.length * after.length > MAX_COMPARED_CELLS)
    return { blocks: [], changes: 0, comparable: false, added: 0, removed: 0 };

  const blocks: DiffBlock[] = [];
  let section = "";
  let changes = 0;
  let added = 0;
  let removed = 0;
  let openChange: number | null = null;

  // Counted here rather than from the finished blocks. A changed table row is
  // one merged block holding both sides at once, so counting blocks afterwards
  // would lose every edit made inside a table.
  for (const step of walk(before, after)) {
    if (step.kind === "added") added += 1;
    if (step.kind === "removed") removed += 1;
    // A heading names the section it opens whether it is new or not. Only a
    // same-side heading used to count, so every passage under a heading the
    // proposal ADDED was labelled with the section above it, and the stepper
    // said the reader was somewhere they were not. A removed heading is
    // leaving, so it does not name anything.
    const heading = HEADING.exec(step.line)?.[1];
    if (heading && step.kind !== "removed") section = heading;
    if (step.kind === "same") {
      // A blank line does not end a change: a reworded paragraph and the
      // bullet beneath it are one edit, and splitting them makes two.
      if (step.line.trim()) openChange = null;
    } else if (openChange === null) {
      changes += 1;
      openChange = changes - 1;
    }
    const change = step.kind === "same" ? null : openChange;
    const last = blocks[blocks.length - 1];
    // A block belongs to one section. Without that, a run of added lines that
    // opened with a new heading kept the name of the section above it for all
    // of its lines, including the heading's own.
    if (
      last &&
      last.kind === step.kind &&
      last.change === change &&
      last.section === section
    )
      last.lines.push(step.line);
    else blocks.push({ kind: step.kind, lines: [step.line], change, section });
  }

  // Pair each removed passage with the added one that replaced it, so the words
  // that moved can be marked inside both.
  for (let i = 0; i < blocks.length - 1; i += 1) {
    const from = blocks[i];
    const to = blocks[i + 1];
    if (from.kind !== "removed" || to.kind !== "added") continue;
    if (from.change !== to.change || from.lines.length !== to.lines.length)
      continue;
    // Table cells receive their own marks when the rows are combined below.
    const removed = from.lines.map((line, n) =>
      TABLE_ROW.test(line) ? line : markWords(line, to.lines[n], "removed"),
    );
    const added = to.lines.map((line, n) =>
      TABLE_ROW.test(line) ? line : markWords(from.lines[n], line, "added"),
    );
    from.lines = removed;
    to.lines = added;
  }
  const merged = mergeTables(blocks);
  // A merged table is one passage, even when separate rows changed.
  const changeIds = new Map<number, number>();
  for (const block of merged) {
    if (block.change === null) continue;
    if (!changeIds.has(block.change))
      changeIds.set(block.change, changeIds.size);
    block.change = changeIds.get(block.change)!;
  }
  return {
    blocks: merged,
    changes: changeIds.size,
    comparable: true,
    added,
    removed,
  };
};
